import { createContext, useContext, useEffect, useState } from 'react';
import i18n, { SUPPORTED_LOCALES, readStoredLocale, writeStoredLocale } from '@/lib/i18n';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { useAuth } from './auth-context';

const LocaleContext = createContext(null);

const LOCALE_NAMES = {
    fr: 'Français',
    en: 'English',
    es: 'Español',
};

/** Vrai si ?lang=xx est présent : il court-circuite localStorage comme le compte. */
function hasLangQueryParam() {
    return Boolean(new URLSearchParams(window.location.search).get('lang'));
}

/**
 * Si l'URL porte ?lang=xx, l'aligne sur la langue choisie — sans navigation.
 *
 * ?lang=xx est prioritaire sur localStorage dans resolveInitialLocale() : le
 * laisser pointer sur l'ancienne langue annulerait le choix au prochain
 * chargement. replaceState suffit : react-router n'écoute que popstate, et seul
 * le query param change.
 */
function syncLangQueryParam(locale) {
    const url = new URL(window.location.href);
    if (!url.searchParams.get('lang')) return;
    url.searchParams.set('lang', locale);
    window.history.replaceState(window.history.state, '', url.toString());
}

/**
 * Applique une langue à chaud : i18next notifie `languageChanged`, ce qui
 * re-rend tout composant abonné via useTranslation() (nouveau `t`) et met à
 * jour l'état de LocaleProvider (cf. son effet d'abonnement).
 *
 * Les ressources sont embarquées dans le bundle (cf. lib/i18n/index.js), donc
 * changeLanguage() n'a rien à charger : la promesse est résolue immédiatement.
 */
function applyLocale(locale, source) {
    writeStoredLocale(locale, source);
    return i18n.changeLanguage(locale);
}

/**
 * Aligne la langue affichée sur celle enregistrée sur le compte.
 *
 * Appelée par AuthProvider dès que GET /user a répondu (`user.locale`, null si
 * l'utilisateur n'a jamais choisi). Trois cas :
 * - le compte porte une autre langue que celle affichée (nouvel appareil,
 *   localStorage vidé) → le compte fait foi : on bascule à chaud ;
 * - le compte n'en porte aucune alors qu'une langue a été choisie ici → on la
 *   remonte, pour qu'elle suive sur les autres appareils ;
 * - les deux concordent → rien à faire.
 *
 * @param {{ locale?: string|null }|null} user - utilisateur renvoyé par GET /user
 */
export function reconcileLocaleWithUser(user) {
    // ?lang=xx est prioritaire dans resolveInitialLocale() : imposer la langue
    // du compte serait annulé au prochain chargement.
    if (hasLangQueryParam()) return;

    const serverLocale = user?.locale;

    if (!serverLocale || !SUPPORTED_LOCALES.includes(serverLocale)) {
        const stored = readStoredLocale();
        // Seul un choix fait ici se remonte : une langue déjà descendue du
        // compte n'a rien à lui réapprendre, et la langue du navigateur n'est
        // pas un choix.
        // Best-effort : que la préférence ne remonte pas n'empêche pas
        // l'utilisateur de travailler dans la langue qu'il voit déjà.
        if (stored?.source === 'device') horodatyApi.updateLocale(stored.locale).catch(() => {});
        return;
    }

    if (serverLocale === i18n.language) return;

    applyLocale(serverLocale, 'account');
}

/**
 * Provider de langue pour l'application.
 *
 * La résolution initiale (query param > localStorage > navigateur > fallback)
 * a déjà eu lieu au chargement du module lib/i18n/index.js, avant le premier
 * rendu — donc avant même que ce provider ne soit monté. Son rôle ici :
 * - refléter la langue active dans un état React (sélecteurs, <html lang>) ;
 * - exposer setLocale() pour le sélecteur de langue, qui bascule à chaud.
 *
 * Le changement se fait sans recharger la page. Ce que ça suppose du reste du
 * code :
 * - aucune constante de module ne fige un i18n.t() à l'import — les tables de
 *   libellés sont des fonctions (`getFieldTypes()`, `getRoles()`…) appelées au
 *   rendu ;
 * - tout composant qui traduit est abonné via useTranslation() ;
 * - les useMemo/useCallback qui traduisent ont `t` dans leurs dépendances : le
 *   `t` de react-i18next est figé sur la langue du rendu qui l'a produit, et
 *   change d'identité au changement de langue ;
 * - les chargeurs qui stockent en état des libellés ou des dates formatées
 *   (dashboard, explorateur de dossiers) se relancent quand `t` change.
 *
 * La langue du compte, elle, est rapprochée par reconcileLocaleWithUser(),
 * appelée par AuthProvider dès que GET /user a répondu.
 *
 * Doit être monté sous AuthProvider : setLocale() a besoin de savoir s'il y a
 * une session pour remonter le choix au compte. Pas le global PHP
 * window.__AUTH_STATE__ pour ça : c'est un instantané pris au chargement de la
 * page, que la connexion en SPA (navigate() sans rechargement) laisse à false.
 */
export function LocaleProvider({ children }) {
    const { isAuthenticated } = useAuth();
    const [locale, setLocaleState] = useState(i18n.language);

    // Source de vérité : i18next. Le provider suit ses changements, d'où qu'ils
    // viennent (sélecteur, reconcileLocaleWithUser après GET /user).
    useEffect(() => {
        const onChanged = (lng) => setLocaleState(lng);
        i18n.on('languageChanged', onChanged);
        return () => i18n.off('languageChanged', onChanged);
    }, []);

    useEffect(() => {
        document.documentElement.lang = locale;
    }, [locale]);

    /**
     * Change la langue à chaud et l'enregistre (navigateur, puis compte si connecté).
     *
     * Le choix est écrit en localStorage avant tout : c'est lui que
     * resolveInitialLocale() relira au prochain chargement. La remontée au
     * compte est best-effort — une erreur réseau ne doit pas empêcher de
     * travailler dans la langue qu'on vient de choisir.
     *
     * Choisir en étant connecté, c'est choisir pour son compte : la langue est
     * marquée 'account' et s'effacera avec la session, comme celle qui en
     * descend. Sans session, elle n'appartient qu'à ce navigateur et y reste.
     */
    const setLocale = async (next) => {
        if (!SUPPORTED_LOCALES.includes(next) || next === i18n.language) return;

        await applyLocale(next, isAuthenticated ? 'account' : 'device');
        syncLangQueryParam(next);

        if (isAuthenticated) horodatyApi.updateLocale(next).catch(() => {});
    };

    return (
        <LocaleContext.Provider
            value={{ locale, setLocale, availableLocales: SUPPORTED_LOCALES, localeNames: LOCALE_NAMES }}
        >
            {children}
        </LocaleContext.Provider>
    );
}

/**
 * Hook pour lire/changer la langue courante.
 *
 * @returns {{ locale: string, setLocale: (locale: string) => Promise<void>, availableLocales: string[], localeNames: Record<string, string> }}
 */
export function useLocale() {
    const context = useContext(LocaleContext);
    if (!context) {
        throw new Error('useLocale must be used within a LocaleProvider');
    }
    return context;
}
